import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Modal,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  ReloadOutlined,
  ShopOutlined,
  CheckOutlined,
  FileOutlined,
  LockOutlined,
} from '@ant-design/icons';
import {
  fetchFirmNamesRequest,
  setFirmNameEditing,
  clearFirmNameEditing
} from '../../../redux/actions/firmNameActions';
import { hasPermission } from '../../../utils/permissions';
import FirmNameForm from './FirmNameForm';
import './FirmName.css';

const { Search } = Input;
const { Option } = Select;

const FirmName = () => {
  const dispatch = useDispatch();
  const { firmNames, loading, totalCount } = useSelector(state => state.firmNames);
  const permissions = useSelector(state => state.auth.permissions || []);
  
  // Check permissions
  const canViewGrid = hasPermission(permissions, 'firm_name/gridView');
  const canCreate = hasPermission(permissions, 'firm_name/create');
  const canEdit = hasPermission(permissions, 'firm_name/edit');
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    is_active: undefined,
    firm_name: '',
    gst_number: '',
    mobile_number: ''
  });

  useEffect(() => {
    if (canViewGrid) {
      dispatch(fetchFirmNamesRequest(filters));
    }
  }, [dispatch, filters, canViewGrid]);

  const handleAddNew = () => {
    dispatch(clearFirmNameEditing());
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    dispatch(setFirmNameEditing(record));
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    dispatch(clearFirmNameEditing());
  };

  const handleRefresh = () => {
    dispatch(fetchFirmNamesRequest(filters));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      is_active: undefined,
      firm_name: '',
      gst_number: '',
      mobile_number: ''
    });
  };

  // If user doesn't have permission to view grid, show message
  if (!canViewGrid) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><LockOutlined /></div>
        <div className="empty-state-text">
          You don't have permission to view firm names.
        </div>
      </div>
    );
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: 'Firm Name',
      dataIndex: 'firm_name',
      key: 'firm_name',
      ellipsis: {
        showTitle: false,
      },
      render: (firm_name) => (
        <Tooltip placement="topLeft" title={firm_name}>
          <strong>{firm_name}</strong>
        </Tooltip>
      ),
    },
    {
      title: 'GST Number',
      dataIndex: 'gst_number',
      key: 'gst_number',
      width: 180,
      render: (gst_number) => (
        <Tooltip title="GST Registration Number">
          <code style={{ fontSize: '12px' }}>{gst_number}</code>
        </Tooltip>
      ),
    },
    {
      title: 'Mobile Number',
      dataIndex: 'mobile_number',
      key: 'mobile_number',
      width: 140,
      render: (mobile_number) => (
        <span style={{ fontFamily: 'monospace' }}>{mobile_number}</span>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      ellipsis: {
        showTitle: false,
      },
      render: (address) => (
        <Tooltip placement="topLeft" title={address}>
          {address}
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (is_active) => (
        <Tag color={is_active ? 'success' : 'error'}>
          {is_active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (created_at) => {
        if (!created_at) return '-';
        return new Date(created_at).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      },
    },
    ...(canEdit ? [{
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div style={{ padding: '1rem' }}>
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><ShopOutlined /></div>
          <div className="stat-content">
            <div className="stat-value">{(firmNames || []).length}</div>
            <div className="stat-label">Total Firms</div>
          </div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#28a745' }}>
          <div className="stat-icon" style={{ background: '#d4edda', color: '#155724' }}><CheckOutlined /></div>
          <div className="stat-content">
            <div className="stat-value">{(firmNames || []).filter(f => f.is_active).length}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#17a2b8' }}>
          <div className="stat-icon" style={{ background: '#d1ecf1', color: '#0c5460' }}><FileOutlined /></div>
          <div className="stat-content">
            <div className="stat-value">{(firmNames || []).filter(f => f.gst_number).length}</div>
            <div className="stat-label">With GST</div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="modern-table-container" style={{ marginTop: '1.5rem' }}>
        <div className="table-header">
          <h2 className="table-title">Firm Names</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <ReloadOutlined /> Refresh
            </button>
            {canCreate && (
              <button className="btn btn-primary" onClick={handleAddNew}>
                <PlusOutlined /> Add New Firm
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px', marginBottom: '1rem' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Search by firm name"
              value={filters.firm_name}
              onChange={(e) => handleFilterChange('firm_name', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="GST Number"
              value={filters.gst_number}
              onChange={(e) => handleFilterChange('gst_number', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Status"
              value={filters.is_active}
              onChange={(value) => handleFilterChange('is_active', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button onClick={clearFilters}>
                Clear Filters
              </Button>
            </Space>
          </Col>
        </Row>
        </div>

        <Table
          columns={columns}
          dataSource={firmNames}
          rowKey="id"
          loading={loading}
          pagination={{
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} firms`,
          }}
          scroll={{ x: 1200 }}
          locale={{
            emptyText: (
              <div className="empty-state">
                <div className="empty-state-icon"><ShopOutlined /></div>
                <div className="empty-state-text">No firm names found</div>
                {canCreate && (
                  <button className="btn btn-primary" onClick={handleAddNew}>
                    Add Your First Firm
                  </button>
                )}
              </div>
            ),
          }}
        />
      </div>

      <Modal
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        destroyOnClose
        centered
      >
        <FirmNameForm onCancel={handleModalClose} />
      </Modal>
    </div>
  );
};

export default FirmName;
